/* eslint-disable */
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Point Cloud Coordinate Transformation Utilities
 *
 * Handles conversion between ForgePotree coordinate space and standalone Potree coordinate space.
 *
 * COORDINATE SYSTEMS:
 *
 * 1. Original Point Cloud Space (file coordinates)
 *    - Units: meters/feet/inches (depends on source)
 *    - Origin: Arbitrary (scanner position)
 *
 * 2. ForgePotree Space (Forge Viewer integration)
 *    - Transformation: position.multiplyScalar(1/unitScale).sub(globalOffset)
 *    - Scale: pointcloud.scale = (scaleFactor, scaleFactor, scaleFactor)
 *    - Units: Forge internal units
 *    - Reference: mainExtension.js:538-540
 *
 * 3. Crop Box Space (THREE.Box3)
 *    - Coordinate system: ForgePotree space
 *    - Format: { min: Vector3, max: Vector3 }
 *
 * 4. Standalone Potree Space (measurement viewer)
 *    - Loads from original file (space #1)
 *    - No global offset applied by default
 *    - Requires inverse transformation of crop box
 *
 * Transformation flow:
 * Original → ForgePotree → Crop Box → Inverse Transform → Potree Space
 */

const THREE = window.THREE;

/**
 * Converts a crop box from ForgePotree space to standalone Potree space
 *
 * This function handles the complete inverse transformation including:
 * - Point cloud position offset
 * - Point cloud scale
 * - Point cloud rotation
 * - Unit scale conversion
 * - Global offset removal
 *
 * @param {THREE.Box3} forgeCropBox - Crop box in Forge viewer coordinates
 * @param {Object} forgePCInstance - ForgePotree point cloud instance from mainExtension._pointclouds
 * @param {THREE.Vector3} forgePCInstance.position - Point cloud position in Forge space
 * @param {THREE.Quaternion} forgePCInstance.quaternion - Point cloud rotation
 * @param {THREE.Vector3} forgePCInstance.scale - Point cloud scale
 * @param {number} unitScale - Global unit scale factor (store.state.unitScale)
 * @param {THREE.Vector3} globalOffset - Global coordinate offset (store.state.globalOffset)
 * @returns {THREE.Box3} - Crop box in standalone Potree coordinates
 *
 * @example
 * const forgeBox = new THREE.Box3(
 *   new THREE.Vector3(0, 0, 0),
 *   new THREE.Vector3(10, 10, 10)
 * );
 * const forgePCInstance = mainExtension._pointclouds.get('pipeline_scan');
 * const unitScale = store.state.unitScale;
 * const globalOffset = store.state.globalOffset;
 *
 * const potreeBox = convertForgeBoxToPotreeBox(
 *   forgeBox,
 *   forgePCInstance,
 *   unitScale,
 *   globalOffset
 * );
 */
export function convertForgeBoxToPotreeBox(forgeCropBox, forgePCInstance, unitScale, globalOffset) {
  if (!forgeCropBox || !forgePCInstance) {
    throw new Error('Missing required parameters: forgeCropBox and forgePCInstance are required');
  }

  // Validate THREE.Box3
  if (!forgeCropBox.min || !forgeCropBox.max) {
    throw new Error('Invalid crop box: must be a THREE.Box3 with min and max properties');
  }

  // Default values
  unitScale = unitScale || 1;
  globalOffset = globalOffset || new THREE.Vector3(0, 0, 0);
  const scaleFactor = 1 / unitScale;

  const quat = forgePCInstance.quaternion || new THREE.Quaternion();
  const isIdentityRotation = Math.abs(quat.x) < 0.0001 &&
                             Math.abs(quat.y) < 0.0001 &&
                             Math.abs(quat.z) < 0.0001 &&
                             Math.abs(quat.w - 1) < 0.0001;

  try {
    // Transform all 8 corners of the crop box
    // We need all corners because rotation might change which corners are min/max
    const corners = [
      new THREE.Vector3(forgeCropBox.min.x, forgeCropBox.min.y, forgeCropBox.min.z),
      new THREE.Vector3(forgeCropBox.max.x, forgeCropBox.min.y, forgeCropBox.min.z),
      new THREE.Vector3(forgeCropBox.min.x, forgeCropBox.max.y, forgeCropBox.min.z),
      new THREE.Vector3(forgeCropBox.max.x, forgeCropBox.max.y, forgeCropBox.min.z),
      new THREE.Vector3(forgeCropBox.min.x, forgeCropBox.min.y, forgeCropBox.max.z),
      new THREE.Vector3(forgeCropBox.max.x, forgeCropBox.min.y, forgeCropBox.max.z),
      new THREE.Vector3(forgeCropBox.min.x, forgeCropBox.max.y, forgeCropBox.max.z),
      new THREE.Vector3(forgeCropBox.max.x, forgeCropBox.max.y, forgeCropBox.max.z)
    ];

    /**
     * TRANSFORMATION FORMULA:
     *
     * From mainExtension.js:556-557:
     *   pointcloud.position.multiplyScalar(scaleFactor).sub(globalOffset)
     *   pointcloud.scale = scaleFactor
     *
     * This means:
     *   ForgePotree_world = (Standalone_world × scaleFactor) - globalOffset
     *
     * Therefore, the inverse transformation is:
     *   Standalone_world = (ForgePotree_world + globalOffset) ÷ scaleFactor
     *
     * If there's rotation (non-identity quaternion), we need to handle it differently
     * using the full inverse matrix transformation.
     */

    if (isIdentityRotation) {
      // Simple case: No rotation, just translation and scale
      corners.forEach((corner) => {
        // Step 1: Add globalOffset
        corner.add(globalOffset);

        // Step 2: Divide by scaleFactor (if not 1)
        if (Math.abs(scaleFactor - 1) > 0.0001) {
          corner.divideScalar(scaleFactor);
        }
      });
    } else {
      // Complex case: Has rotation, use full matrix transformation
      const forgeMatrix = new THREE.Matrix4();
      forgeMatrix.compose(
        forgePCInstance.position || new THREE.Vector3(0, 0, 0),
        quat,
        forgePCInstance.scale || new THREE.Vector3(1, 1, 1)
      );

      const inverseMatrix = forgeMatrix.clone().invert();

      corners.forEach((corner) => {
        corner.applyMatrix4(inverseMatrix);
      });
    }

    // Create new box from transformed corners
    const potreeBox = new THREE.Box3();
    potreeBox.setFromPoints(corners);

    return potreeBox;
  } catch (error) {
    debugError('PointCloudCoordinates', 'Transformation error:', error);
    throw new Error(`Failed to transform crop box: ${error.message}`);
  }
}

/**
 * Creates a Potree BoxVolume from a THREE.Box3
 *
 * Potree's clipping system uses BoxVolume objects which have:
 * - position (center of the box)
 * - scale (size of the box)
 * - clip (boolean to enable clipping)
 * - clipMode (CLIP_OUTSIDE or CLIP_INSIDE)
 *
 * @param {THREE.Box3} box3 - THREE.js bounding box
 * @param {string} clipMode - 'outside' (show only inside) or 'inside' (show only outside)
 * @returns {Potree.BoxVolume} - Potree clipping volume
 *
 * @example
 * const box3 = new THREE.Box3(
 *   new THREE.Vector3(-5, -5, -5),
 *   new THREE.Vector3(5, 5, 5)
 * );
 * const clipVolume = createPotreeClipVolume(box3);
 * viewer.scene.addClipVolume(clipVolume);
 */
export function createPotreeClipVolume(box3, clipMode = 'outside') {
  // Check if Potree is available
  if (typeof window.Potree === 'undefined') {
    throw new Error('Potree library not loaded. Ensure window.Potree is available.');
  }

  if (!window.Potree.BoxVolume) {
    throw new Error('Potree.BoxVolume not available. Check Potree version compatibility.');
  }

  // Validate box3
  if (!box3 || !box3.min || !box3.max) {
    throw new Error('Invalid box3: must be a THREE.Box3 with min and max properties');
  }

  try {
    // Create Potree BoxVolume
    const volume = new window.Potree.BoxVolume();

    // Calculate center and size
    const center = box3.getCenter(new THREE.Vector3());
    const size = box3.getSize(new THREE.Vector3());

    // Set volume properties
    volume.position.copy(center);
    volume.scale.copy(size);

    // Enable clipping
    volume.clip = true;

    // Note: There is no clipMode property on the volume itself.
    // The clip behavior is controlled by clipTask instead!
    // We'll set that when adding the volume to the scene.

    // Debug logging
    debugLog('PointCloudCoordinates', 'Created Potree clip volume:', {
      position: volume.position,
      scale: volume.scale,
      clip: volume.clip,
      clipMode: clipMode
    });

    return volume;
  } catch (error) {
    debugError('PointCloudCoordinates', 'Failed to create clip volume:', error);
    throw new Error(`Failed to create Potree clip volume: ${error.message}`);
  }
}

/**
 * Validates that a crop box contains actual volume (not degenerate)
 *
 * @param {THREE.Box3} box - Box to validate
 * @param {number} minVolume - Minimum acceptable volume (default: 0.001)
 * @returns {boolean} - True if box is valid
 */
export function isValidCropBox(box, minVolume = 0.001) {
  if (!box || !box.min || !box.max) {
    return false;
  }

  const size = box.getSize(new THREE.Vector3());
  const volume = size.x * size.y * size.z;

  if (volume < minVolume) {
    debugWarn('PointCloudCoordinates', 'Crop box volume too small:', volume);
    return false;
  }

  // Check for infinity or NaN
  if (!isFinite(volume)) {
    debugWarn('PointCloudCoordinates', 'Crop box has invalid volume:', volume);
    return false;
  }

  return true;
}

/**
 * Calculates the volume of a crop box in cubic units
 *
 * @param {THREE.Box3} box - Box to measure
 * @returns {number} - Volume in cubic units
 */
export function calculateCropBoxVolume(box) {
  if (!box || !box.min || !box.max) {
    return 0;
  }

  const size = box.getSize(new THREE.Vector3());
  return size.x * size.y * size.z;
}

/**
 * Formats crop box dimensions for display
 *
 * @param {THREE.Box3} box - Box to format
 * @param {number} precision - Decimal places (default: 2)
 * @returns {string} - Formatted string like "10.50 × 8.25 × 6.75"
 */
export function formatCropBoxSize(box, precision = 2) {
  if (!box || !box.min || !box.max) {
    return 'N/A';
  }

  const size = box.getSize(new THREE.Vector3());
  return `${size.x.toFixed(precision)} × ${size.y.toFixed(precision)} × ${size.z.toFixed(precision)}`;
}

/**
 * Converts a Potree BoxVolume to a THREE.Box3 in Forge coordinate space
 *
 * This function extracts the world-space bounding box from a Potree BoxVolume
 * and applies the global offset to convert it to Forge coordinates.
 *
 * @param {Potree.BoxVolume} boxVolume - The Potree box volume from the scene
 * @param {THREE.Vector3} offset - Global offset to subtract (default: 0,0,0)
 * @returns {THREE.Box3} - Bounding box in Forge coordinate space
 *
 * @example
 * const boxVolume = AVA_POTREE_VIEWER.scene.volumes[0];
 * const globalOffset = vuexStore.state.viewer.model.getGlobalOffset();
 * const forgeBox = getBox3FromBoxVolume(boxVolume, globalOffset);
 */
export function getBox3FromBoxVolume(boxVolume, offset = new THREE.Vector3(0, 0, 0)) {
  if (!boxVolume) {
    throw new Error('BoxVolume is required');
  }

  // Update world matrix
  boxVolume.updateMatrixWorld(true);

  // Extract scale, rotation, and position from world matrix
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  boxVolume.matrixWorld.decompose(position, quaternion, scale);

  // Get the 8 corners of a unit cube (BoxGeometry is 1x1x1, so -0.5 to 0.5)
  const halfSize = 0.5;
  const corners = [
    new THREE.Vector3(-halfSize, -halfSize, -halfSize),
    new THREE.Vector3( halfSize, -halfSize, -halfSize),
    new THREE.Vector3(-halfSize,  halfSize, -halfSize),
    new THREE.Vector3( halfSize,  halfSize, -halfSize),
    new THREE.Vector3(-halfSize, -halfSize,  halfSize),
    new THREE.Vector3( halfSize, -halfSize,  halfSize),
    new THREE.Vector3(-halfSize,  halfSize,  halfSize),
    new THREE.Vector3( halfSize,  halfSize,  halfSize)
  ];

  // Transform all corners to world space (applies scale, rotation, and position)
  corners.forEach(corner => corner.applyMatrix4(boxVolume.matrixWorld));

  // Apply offset
  corners.forEach(corner => corner.sub(offset));

  // Create Box3 from transformed corners
  const box3 = new THREE.Box3();
  box3.setFromPoints(corners);

  return box3;
}

/**
 * Safely retrieves the active crop box volume from the Potree scene
 *
 * @param {Potree.Viewer} potreeViewer - The Potree viewer instance
 * @returns {Potree.BoxVolume|null} - The first active clipping BoxVolume or null if none found
 *
 * @example
 * const cropVolume = getActiveCropBoxVolume(AVA_POTREE_VIEWER);
 * if (cropVolume) {
 *   const box3 = getBox3FromBoxVolume(cropVolume, globalOffset);
 * }
 */
export function getActiveCropBoxVolume(potreeViewer) {
  if (!potreeViewer || !potreeViewer.scene || !potreeViewer.scene.volumes) {
    debugWarn('PointCloudCoordinates', 'Potree viewer or scene volumes not available');
    return null;
  }

  try {
    const volumes = potreeViewer.scene.volumes;
    const cropVolume = volumes.find(v => (v.clip && v instanceof window.Potree.BoxVolume));

    if (!cropVolume) {
      debugWarn('PointCloudCoordinates', 'No active crop box volume found in scene');
      return null;
    }

    return cropVolume;
  } catch (error) {
    debugError('PointCloudCoordinates', 'Error getting active crop box volume:', error);
    return null;
  }
}

/**
 * Debug helper: Visualizes transformation by logging key points
 *
 * @param {THREE.Box3} forgeBox - Box in Forge space
 * @param {THREE.Box3} potreeBox - Box in Potree space
 * @param {Object} forgePCInstance - Point cloud instance
 */
export function debugTransformation(forgeBox, potreeBox, forgePCInstance) {
  debugLog('PointCloudCoordinates', '📊 Point Cloud Coordinate Transformation Debug');

  debugLog('PointCloudCoordinates', 'Forge Crop Box (Input):', {
    min: { ...forgeBox.min },
    max: { ...forgeBox.max },
    center: forgeBox.getCenter(new THREE.Vector3()),
    size: forgeBox.getSize(new THREE.Vector3())
  });

  debugLog('PointCloudCoordinates', 'ForgePotree Instance:', {
    position: { ...forgePCInstance.position },
    rotation: forgePCInstance.rotation ? { ...forgePCInstance.rotation } : 'N/A',
    quaternion: { ...forgePCInstance.quaternion },
    scale: { ...forgePCInstance.scale }
  });

  debugLog('PointCloudCoordinates', 'Potree Crop Box (Output):', {
    min: { ...potreeBox.min },
    max: { ...potreeBox.max },
    center: potreeBox.getCenter(new THREE.Vector3()),
    size: potreeBox.getSize(new THREE.Vector3())
  });
}

// Export all functions
export default {
  convertForgeBoxToPotreeBox,
  createPotreeClipVolume,
  isValidCropBox,
  calculateCropBoxVolume,
  formatCropBoxSize,
  getBox3FromBoxVolume,
  getActiveCropBoxVolume,
  debugTransformation
};
